import React, { useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { useForm } from 'react-hook-form';
import DateService from 'services/DateService/dateService';
import * as S from './InspectionComments.styles';
import { IInspectionCommentsProps, IInspectionCommentForm } from './InspectionComments.types';

const InspectionComments = ({ inspection, comments, onCommentAdd, isLoading = false }: IInspectionCommentsProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState, watch } = useForm<IInspectionCommentForm>({ mode: 'onChange' });

  // Observa o valor do campo comment para contador de caracteres
  const commentValue = watch('comment', '');

  const onSubmit = async (data: IInspectionCommentForm): Promise<void> => {
    if (!data.comment.trim()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onCommentAdd(data.comment.trim());
      reset();
    } catch (err) {
      console.error('Erro ao salvar comentário:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calcula caracteres atuais
  const currentLength = commentValue?.length || 0;
  const maxLength = 2000;
  const isNearLimit = currentLength > maxLength * 0.9; // 90% do limite

  return (
    <S.Container data-testid="inspection-comments">
      <S.Title>Comentários ({comments.length})</S.Title>

      <S.ChatArea data-testid="comments-chat-area">
        {isLoading ? (
          <S.EmptyState>
            <CircularProgress size={40} />
            <S.EmptyStateText>Carregando comentários...</S.EmptyStateText>
          </S.EmptyState>
        ) : comments.length > 0 ? (
          <>
            {comments.map((comment) => (
              <S.CommentWrapper key={comment.id}>
                <S.CommentHeader>
                  <S.CommentAuthor>{comment.userName || 'Usuário'}</S.CommentAuthor>
                  <S.CommentDate>{DateService.formatDateAndTime(comment.createdAt)}</S.CommentDate>
                </S.CommentHeader>
                <S.CommentText>{comment.text}</S.CommentText>
                {comment.updatedAt && comment.updatedAt !== comment.createdAt && (
                  <S.CommentDate style={{ fontSize: '10px', marginTop: '4px' }}>(editado)</S.CommentDate>
                )}
              </S.CommentWrapper>
            ))}
          </>
        ) : (
          <S.EmptyState>
            <S.EmptyStateText>
              <b>Nenhum comentário adicionado</b>
            </S.EmptyStateText>
            <S.EmptyStateText>Seja o primeiro a comentar nesta inspeção</S.EmptyStateText>
          </S.EmptyState>
        )}
      </S.ChatArea>

      <S.Form data-testid="comment-form" onSubmit={handleSubmit(onSubmit)}>
        <S.InputWrapper>
          <S.TextField
            id="comment"
            name="comment"
            placeholder="Digite seu comentário..."
            variant="outlined"
            disabled={isSubmitting || isLoading}
            multiline
            rows={2}
            inputRef={register({
              required: true,
              maxLength: maxLength,
            })}
            inputProps={{
              'data-testid': 'input-comment',
              maxLength: maxLength,
            }}
          />
          <S.CharacterCounter isNearLimit={isNearLimit}>
            {currentLength}/{maxLength}
          </S.CharacterCounter>
        </S.InputWrapper>

        <S.SendButton
          type="submit"
          data-testid="send-comment-button"
          disabled={isSubmitting || isLoading || !formState.isValid || !commentValue?.trim()}
          aria-label="Enviar comentário"
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        </S.SendButton>
      </S.Form>
    </S.Container>
  );
};

export default InspectionComments;
